import React, { useState, useEffect, useRef } from "react";
import ModalPage from "components/explore/layout/ModalPage";
import { withStyles } from "@material-ui/core/styles";
import {
  Typography,
  Tooltip,
  SvgIcon,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  Collapse
} from "@material-ui/core";
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { fetchExploreViews, createExploreView, requestExploreSelectedViewIdChange, deleteExploreView, 
  setConfirmationModalState, setExploreSelectedViewIsOpen, appNotify } from "store/actions";
import { connect } from "react-redux";
import { compose } from "recompose";
import { Paging, Column, HeaderFilter, SearchPanel } from "devextreme-react/data-grid";
import Stack from "components/layout/Stack";
import { sendClientLog } from 'services/logService';
import 'components/explore/views/viewsManagerGrid.css';
import DataGrid from 'components/devExtreme/DataGrid';
import { Constants } from 'utilities/constants';
import { LoadingOverlayWrapper } from 'components/layout';

const pageSize = 10;

function ViewsManager({ classes, user, fetchExploreViews, createExploreView, deleteExploreView, setExploreSelectedViewIsOpen,
  modalOpen, shouldCollapseGrid, requestExploreSelectedViewIdChange, appNotify, viewsLoadingState,
  views, onClose, hasSelectedView, setConfirmationModalState, isMobile, isSideBarOpen
}) {
  const gridRef = useRef(null);
  const [viewNameInput, setViewNameInput] = useState('');
  const [viewNameError, setViewNameError] = useState('');
  const [viewPrivacy, setViewPrivacy] = useState('Private');
  const [creationType, setCreationType] = useState('new');
  const [saveViewExpanded, setSaveViewExpanded] = useState(true);
  const [gridExpanded, setGridExpanded] = useState(true);

  useEffect(() => {
    if (modalOpen) {
      fetchExploreViews();
      
      if (shouldCollapseGrid) {
        setSaveViewExpanded(true);
        setGridExpanded(false);
      } else {
        setGridExpanded(true);
      }
    }
  }, [modalOpen, shouldCollapseGrid]);

  const setDefaults = () => {
    setViewNameInput('');
    setViewPrivacy('Private');
    setCreationType('new');
  }

  const applyAndCloseSavedViewClick = (e) => {
    applySavedView(e);
    sendClientLog('Info',`Explore Views - User clicked Apply & Close on ViewsManager grid for view id ${e.row.data.id}`);
    onClose();
  };

  const applySavedViewClick = (e) => {
    applySavedView(e);
    sendClientLog('Info',`Explore Views - User clicked Apply on ViewsManager grid for view id ${e.row.data.id}`);
  };

  const applySavedView = (e) => {
    console.log(e)
    requestExploreSelectedViewIdChange(e.row.data.id);
  };

  const CommandColumnRender = (e) => {
    return (
      <Stack horizontal>
        <Tooltip title={"Apply"} placement="bottom">
          <SvgIcon
            onClick={() => {
              applySavedViewClick(e);
            }}
            className={classes.btnApply}
          >
            <path d="M19.78,2.2L24,6.42L8.44,22L0,13.55L4.22,9.33L8.44,13.55L19.78,2.2M19.78,5L8.44,16.36L4.22,12.19L2.81,13.55L8.44,19.17L21.19,6.42L19.78,5Z" />
          </SvgIcon>
        </Tooltip>
        <Tooltip title={"Apply & Close"} placement="bottom">
          <SvgIcon
            onClick={() => {
              applyAndCloseSavedViewClick(e);
            }}
            className={classes.btnApply}
          >
            <path
              d="M19.78,2.2L24,6.42L8.44,22L0,13.55L4.22,9.33L8.44,13.55L19.78,2.2M19.78,5L8.44,16.36L4.22,12.19L2.81,13.55L8.44,19.17L21.19,6.42L19.78,5Z
                        M14.46,15.88L15.88,14.46L18,16.59L20.12,14.46L21.54,15.88L19.41,18L21.54,20.12L20.12,21.54L18,19.41L15.88,21.54L14.46,20.12L16.59,18L14.46,15.88"
            />
          </SvgIcon>
        </Tooltip>
      </Stack>
    );
  };

  const deleteColumnRender = e => {
    if (e.data.canDelete === false) return null;
    return (
      <Tooltip title={"Delete"} placement="bottom">
        <IconButton
        style={{
          padding: 2,
          margin: -2
        }}
          onClick={() => handleViewDelete(e)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
  )
  }

  const handleViewDelete = e => {
    const { id } = e.data;
    console.log(id)
    setConfirmationModalState({
			isModalOpen: true,
      handleConfirmation: () => {
				closeConfirmationModal();
        deleteExploreView(id);
        sendClientLog('Info',`Explore Views - User clicked and confirmed Delete on ViewsManager grid for view id ${id}`);
			},
      handleCancellation: () => {
				closeConfirmationModal();
			},
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete this view?`
		})
  }
  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  const handleViewNameInputChange = e => {
    const maxLength = Constants.ViewsCharLimit.VIEWNAME;
    if (e.target.value.length > maxLength && !viewNameError) {
      setViewNameError(`Length must be ${maxLength} characters or less`);
    }
    if (viewNameError?.length && e.target.value.length <= maxLength) {
      setViewNameError('');
    }
    setViewNameInput(e.target.value);
  }

  const handleViewPrivacyChange = e => {
    setViewPrivacy(e.target.value);
  }

  const onCreateNewViewClick = () => {
    if (viewNameInput === '') {
      setViewNameError('View name is required');
      return;
    }
    const newView = { name: viewNameInput, privacy: viewPrivacy, fromExisting: creationType === 'fromExisting' };
    createExploreView(newView);
    sendClientLog('Info',`Explore Views - User clicked Create on ViewsManager. Name: ${newView.name}, Privacy: ${newView.privacy}, FromExisting: ${newView.fromExisting}`);
    setDefaults();
    onClose();
  }

  const handleRefreshViewsClick = () => {
    fetchExploreViews();
    sendClientLog('Info',`Explore Views - User refreshed Views from ViewsManager button`);
  }

  const { isLoading, message } = viewsLoadingState;
  return (
    <ModalPage
      maxWidth="lg"
      fullScreen={isMobile && isSideBarOpen}
      title="Views Manager"
      open={modalOpen}
      onClose={onClose}
    >
      <div style={{ display: 'flex' }}>
        <IconButton
          className={classes.expandButton}
          style={{transform: `rotate(${saveViewExpanded ? '0' : '-90'}deg)`,}}
          onClick={() => setSaveViewExpanded(!saveViewExpanded)}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant="subtitle1">Create New View</Typography>
      </div>
      <Collapse in={saveViewExpanded}>
        <div className={isMobile ? classes.createNewViewFormMobile : classes.createNewViewForm}>
          <TextField
            autoFocus={shouldCollapseGrid}
            label='View Name'
            placeholder='Enter a name...'
            onChange={handleViewNameInputChange}
            onBlur={() => setViewNameInput(viewNameInput.trim())}
            value={viewNameInput}
            style={{width: isMobile ? 'unset' : 346, height: isMobile? 'unset' : 68, marginBottom: isMobile ? '15px' : 'unset', marginRight: 16}}
            InputLabelProps={{
              shrink: true,
            }}
            error={viewNameError?.length > 0}
            helperText={viewNameError}
          />
          <FormControl component="fieldset" style={{marginRight: 32, marginBottom: 12}}>
            <FormLabel component="legend">Privacy</FormLabel>
            <RadioGroup row aria-label="privacy" name="privacy" value={viewPrivacy} onChange={handleViewPrivacyChange} style={{flexWrap: 'nowrap', marginLeft: isMobile ? '10px' : ''}}>
              <FormControlLabel value="Private" style={{marginRight: isMobile ? '13px' : '', marginTop: isMobile ? '3px' : ''}} control={<Radio size='small' style={{padding: 4}}/>} label="Private" />
              <FormControlLabel value="Public" style={{marginRight: isMobile ? '13px' : '', marginTop: isMobile ? '3px' : ''}} control={<Radio size='small' style={{padding: 4}}/>} label="Public" />
            </RadioGroup>
          </FormControl>
          <div style={{flexGrow: 1, textAlign: 'end'}}>
            {/* <Typography variant='caption'>
              {!hasSomeFilter
                  ? 'Apply a data filter or geo filter to enable saving.'
                  : ''
              }
            </Typography> */}
          </div>
          <Button
            variant='outlined'
            color='primary'
            onClick={onCreateNewViewClick}
            disabled={isLoading || viewNameError?.length > 0}
            style={{margin: '0 8px'}}
          >
            Create
          </Button>
        </div>
      </Collapse>
      <div className={classes.spacer} />
      <div style={{display: 'flex'}}>
        <IconButton
          className={classes.expandButton}
          style={{transform: `rotate(${gridExpanded ? '0' : '-90'}deg)`, zIndex: 1}}
          onClick={() => setGridExpanded(!gridExpanded)}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant="subtitle1" style={{zIndex: 1}}>Saved Views</Typography>
        {gridExpanded &&
          <IconButton
            className={classes.refreshButton}
            onClick={handleRefreshViewsClick}
            style={{zIndex: 1}}
            disabled={isLoading}
          >
            <RefreshIcon />
          </IconButton>
        }
      </div>
      <Collapse in={gridExpanded}>
        <LoadingOverlayWrapper
          className={classes.overlay}
          active={isLoading}
          message={message}
        >
          <DataGrid
            id='viewsManagerGrid'
            ref={gridRef}
            keyExpr={'id'}
            persistState
            dataSource={views?.length ? views.map(view => {
              return {
                ...view,
                canDelete: user.roles.administrator || view.createdByUserId === user.id,
                sharedWithCount: view.privacy === 'Private' ? view.sharedWithUsers?.length ?? 0 : null // Public views can't be shared with specific users
              }
            }) : []}
            height={466}
            style={{ marginTop: -40 }}
          >
            <Column width={75} cellRender={CommandColumnRender} />
            <Column dataField={"name"} />
            <Column dataField={"createdOn"} width={126} dataType={"date"} sortOrder={"desc"} format={"MM/dd/yyyy"} />
            <Column dataField={"createdBy"} width={140} />
            <Column dataField={"updatedOn"} width={126} dataType={"date"} format={"MM/dd/yyyy"} />
            <Column dataField={"updatedBy"} width={140} />
            <Column dataField={"privacy"} width={91} allowFiltering={true} />
            <Column dataField={"sharedWithCount"} width={102} caption={"Shared With"} dataType={"number"} allowFiltering={false} alignment='left' />
            <Column cellRender={deleteColumnRender} alignment='center' width={48} />
            <Paging defaultPageSize={pageSize} />
            <HeaderFilter
              visible={true}
              allowSearch={true}
              hideSelectAllOnSearch={false}
            />
            <SearchPanel
              visible={true}
              searchVisibleColumnsOnly={true}
            />
          </DataGrid>
        </LoadingOverlayWrapper>
        {/* {(!views?.length || views?.length <= pageSize) &&
          <div className={classes.spacer} />
        } */}
      </Collapse>
    </ModalPage>
  );
}

const styles = (theme) => ({
  locationToggleContainer: {
    margin: '-44px 0px 1px 4px',
    display: 'flex',
    alignItems: 'center'
  },
  tooltipIcon: {
    zIndex: 1,
    fontSize: 16,
    marginLeft: 4
  },
  footnote: {
    paddingTop: 10,
  },
  spacer: {
    height: 16,
  },
  savedFilterButtons: {
    fontSize: 12,
  },
  btnApply: {
    marginRight: 10,
    padding: 2,
    background: "rgba(0, 0, 0, 0.1)",
    borderRadius: `5px`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      cursor: `pointer`,
    },
  },
  refreshButton: {
    padding: 2,
    marginLeft: 6
  },
  queryBuilder: {
    '& div.e-rule-field': {
      display: 'flex'
    },
    '& div.e-rule-filter': {
      width: '400px !important'
    },
    '& div.e-rule-value-delete': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginRight: 6
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 426
  },
  createNewViewForm: {
    display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', padding: '8px 8px 4px'
  },
  createNewViewFormMobile: {
    display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0', padding: '10px'
  }
});

function mapState(state) {
  const { exploreViews, appState } = state;
  return {
    user: appState.user,
    views: exploreViews.views,
    viewsLoadingState: exploreViews.viewsLoadingState,
    hasSelectedView: exploreViews.selectedViewId != null,
    isSideBarOpen: exploreViews.isSideBarOpen,
    isMobile: appState.isMobile,

  };
}

const mapDispatch = {
  fetchExploreViews,
  createExploreView,
  requestExploreSelectedViewIdChange,
  deleteExploreView,
  setConfirmationModalState,
  setExploreSelectedViewIsOpen,
  appNotify
};

export default compose(withStyles(styles), connect(mapState, mapDispatch))(ViewsManager);
